import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    CASE_TYPE_OPTIONS,
    POLE_OPTIONS,
    STATUS_OPTIONS,
    LegalCaseType,
    Pole,
    LegalCaseStatus,
    type SelectOption,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

interface UseLegalCasePageFormArgs {
    onClose: () => void;
    onCreated: () => void;
}

export default function useLegalCasePageForm({ onClose, onCreated }: UseLegalCasePageFormArgs) {
    const [isCreating, setIsCreating] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);

    const [caseType, setCaseType] = useState<string>(LegalCaseType.JUDICIAL_EXECUTION);
    const [pole, setPole] = useState<string>(Pole.ACTIVE);
    const [status, setStatus] = useState<string>(LegalCaseStatus.OPEN);
    const [creditorEntity, setCreditorEntity] = useState('');
    const [openedAt, setOpenedAt] = useState('');
    const [externalCaseNumber, setExternalCaseNumber] = useState('');
    const [court, setCourt] = useState('');
    const [district, setDistrict] = useState('');
    const [courtDivision, setCourtDivision] = useState('');
    const [state, setState] = useState('');
    const [courtLink, setCourtLink] = useState('');
    const [claimValue, setClaimValue] = useState(0);
    const [description, setDescription] = useState('');

    // Opções dos selects vindas do backend, com fallback local.
    const [caseTypeOptions, setCaseTypeOptions] = useState<SelectOption[]>(CASE_TYPE_OPTIONS);
    const [poleOptions, setPoleOptions] = useState<SelectOption[]>(POLE_OPTIONS);
    const [statusOptions, setStatusOptions] = useState<SelectOption[]>(STATUS_OPTIONS);

    useEffect(() => {
        const loadOptions = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.caseTypes?.length) setCaseTypeOptions(opts.caseTypes);
                if (opts.poles?.length) setPoleOptions(opts.poles);
                if (opts.statuses?.length) setStatusOptions(opts.statuses);
            } catch {
                // silencioso — fallback local já preenche os selects
            }
        };
        loadOptions();
    }, []);

    const handleCreate = async () => {
        setIsCreating(true);
        setErrorFields([]);
        try {
            await recoveryApi.createLegalCase({
                caseType,
                pole,
                status,
                creditorEntity,
                openedAt: openedAt || undefined,
                externalCaseNumber: externalCaseNumber || undefined,
                court: court || undefined,
                district: district || undefined,
                courtDivision: courtDivision || undefined,
                state: state ? state.toUpperCase() : undefined,
                courtLink: courtLink || undefined,
                claimValue: claimValue > 0 ? claimValue : undefined,
                description: description || undefined,
            });
            onCreated();
            onClose();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return;
            }
            if (e instanceof ApiError && e.status === 409) {
                toast.error(e.data?.message || e.message);
                return;
            }
            toast.error(e?.message || 'Erro ao cadastrar caso jurídico');
        } finally {
            setIsCreating(false);
        }
    };

    return {
        isCreating, errorFields,
        caseType, setCaseType,
        pole, setPole,
        status, setStatus,
        creditorEntity, setCreditorEntity,
        openedAt, setOpenedAt,
        externalCaseNumber, setExternalCaseNumber,
        court, setCourt,
        district, setDistrict,
        courtDivision, setCourtDivision,
        state, setState,
        courtLink, setCourtLink,
        claimValue, setClaimValue,
        description, setDescription,
        caseTypeOptions, poleOptions, statusOptions,
        handleCreate,
    };
}
